import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DollarSign, TrendingUp, TrendingDown, Activity, PieChart, Building2,
  RefreshCw, Save, ShieldAlert, ArrowRight, ArrowUpRight, ArrowDownRight,
  AlertCircle, History, Loader2, Info, Users, Filter, Download, Mail,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { toast } from "sonner";
import { ProvenanceButton } from "@/components/common/ProvenanceButton";
import { DataStatusChip } from "@/components/common/DataStatusChip";
import { AIAgentPanel } from "@/components/ai/AIAgentPanel";
import { MYOBConnectPanel } from "@/components/finance/MYOBConnectPanel";
import PLSummaryPanel from "@/components/finance/PLSummaryPanel";
import { BUStockSnapshotsPanel } from "@/components/finance/BUStockSnapshotsPanel";
import MYOBSyncHealthStrip from "@/components/finance/MYOBSyncHealthStrip";
import BalanceSheetPanel from "@/components/finance/BalanceSheetPanel";
import { BUExpensesAllocationContent } from "@/pages/FinanceV2Page";
import SalesByBUPanel from "@/components/finance/SalesByBUPanel";
import ClientProfitabilityPanel from "@/components/finance/ClientProfitabilityPanel";
import CashFlowPanel from "@/components/finance/CashFlowPanel";


function KpiCard({ enterpriseId, label, value, sub, icon: Icon, tone = "default", testid, onClick }) {
  const border = tone === "warn"  ? "border-amber-200 bg-amber-50/40"
              : tone === "good"   ? "border-emerald-200 bg-emerald-50/40"
              : tone === "danger" ? "border-rose-200 bg-rose-50/40"
              :                     "border-slate-200 bg-white";
  return (
    <div data-testid={testid} onClick={onClick}
         className={`rounded-lg border ${border} p-4 relative ${onClick ? "cursor-pointer hover:border-gbe hover:shadow-md transition-all" : ""}`}>
      <div className="absolute top-2 right-2"><ProvenanceButton enterpriseId={enterpriseId} size="xs" /></div>
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}


export default function FinancePage() {
  const { can, loaded } = usePermissions();
  const canEditAlloc = can && (can("finance", "edit") || can("org_mgmt", "edit"));
  const [summary, setSummary] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [bu, setBU] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [ar, setAR] = useState(null);
  const [ap, setAP] = useState(null);
  // Read the initial tab from the URL query (?tab=debtors) so deep-links from
  // the Executive Exception panel arrow land on the right tab.
  const location = useLocation();
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get("tab");
    const valid = new Set(["overview", "pnl", "pnl-summary", "balance-sheet",
                            "alloc", "sales-by-bu", "client-profit",
                            "debtors", "creditors"]);
    return valid.has(t) ? t : "overview";
  })();
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, b, al, r, y] = await Promise.all([
        api.get("/finance/v1/summary"),
        api.get("/finance/v1/pnl-series?months=24"),
        api.get("/finance/v1/bu-performance?months=12"),
        api.get("/finance/v1/bu-allocation"),
        api.get("/finance/v1/aged-receivables"),
        api.get("/finance/v1/aged-payables"),
      ]);
      setSummary(s.data);
      setPnl(p.data);
      setBU(b.data);
      setAlloc(al.data);
      setAR(r.data);
      setAP(y.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Finance");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (loaded && can("finance", "view")) load(); /* eslint-disable-next-line */ }, [loaded]);

  if (!loaded) return <AppLayout title="Financial"><div className="h-40 shimmer" /></AppLayout>;
  if (!can("finance", "view")) {
    return (
      <AppLayout title="Financial">
        <div className="max-w-md mx-auto mt-16 text-center">
          <ShieldAlert className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">You don't have access to Financial.</h2>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Financial"
      subtitle="Ver1 module — 48 KPIs · P&L · BU Performance · Direct vs Allocated · Debtors/Creditors"
      actions={
        <div className="flex items-center gap-2">
          <AIAgentPanel agentId="financial" agentName="Financial Agent"
            buttonLabel="Ask Financial Agent" buttonVariant="filled" testid="fin-ai-agent-btn" />
          <NavLink to="/finance/legacy">
            <Button size="sm" variant="outline" data-testid="fin-legacy-btn">Cutover admin</Button>
          </NavLink>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="fin-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      }
    >
      {/* MYOB Sync Health strip — surfaces stale/errored syncs so users
          don't spend time troubleshooting KPI tiles that are simply
          waiting on the next sync cycle. */}
      <MYOBSyncHealthStrip />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <KpiCard enterpriseId="FIN-KPI-001" label={`Revenue ${summary?.fy_label || "FYTD"}`} value={fmtCurrency(summary?.revenue_fytd ?? 0)}       sub="Financial year to date" icon={DollarSign} testid="fin-kpi-revenue" />
        <KpiCard enterpriseId="FIN-KPI-003" label={`Gross Profit ${summary?.fy_label || "FYTD"}`} value={fmtCurrency(summary?.gross_profit_fytd ?? 0)} icon={TrendingUp} testid="fin-kpi-gp" tone="good" />
        <KpiCard enterpriseId="FIN-KPI-004" label="GP % FYTD"        value={`${summary?.gross_profit_pct_fytd ?? 0}%`}    icon={PieChart}   testid="fin-kpi-gppct" />
        <KpiCard enterpriseId="FIN-KPI-005" label={`OpEx ${summary?.fy_label || "FYTD"}`}       value={fmtCurrency(summary?.opex_fytd ?? 0)}         icon={ArrowDownRight} testid="fin-kpi-opex" />
        <KpiCard enterpriseId="FIN-KPI-006" label={`EBITDA ${summary?.fy_label || "FYTD"}`}    value={fmtCurrency(summary?.ebitda_fytd ?? 0)}      icon={Activity}   testid="fin-kpi-ebitda" />
        <KpiCard enterpriseId="FIN-KPI-007" label="Cash Position"   value={fmtCurrency(summary?.cash_position ?? 0)}    icon={DollarSign} testid="fin-kpi-cash" />
      </div>
      {/* Debtors aging strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <KpiCard enterpriseId="FIN-KPI-030" label="Avg Days Sales Outstanding" value={summary?.dso_days != null ? `${Math.round(summary.dso_days)}d` : "—"} sub="Click to view debtors" icon={ArrowUpRight} testid="fin-kpi-dso" tone={(summary?.dso_days ?? 0) >= 60 ? "danger" : (summary?.dso_days ?? 0) >= 45 ? "warn" : "default"} onClick={() => setTab("debtors")} />
        <KpiCard enterpriseId="FIN-KPI-030" label="Total Debtors"   value={fmtCurrency(ar?.total ?? 0)}                 icon={ArrowUpRight} testid="fin-kpi-debtors-total" onClick={() => setTab("debtors")} />
        <KpiCard enterpriseId="FIN-KPI-031" label="Debtors 0-30d"   value={fmtCurrency(ar?.["0_30"] ?? 0)}              icon={ArrowUpRight} testid="fin-kpi-debtors-0-30" />
        <KpiCard enterpriseId="FIN-KPI-032" label="Debtors 31-60d"  value={fmtCurrency(ar?.["31_60"] ?? 0)}             icon={ArrowUpRight} testid="fin-kpi-debtors-31-60" tone={(ar?.["31_60"] ?? 0) > 0 ? "warn" : "default"} />
        <KpiCard enterpriseId="FIN-KPI-033" label="Debtors 61-90d"  value={fmtCurrency(ar?.["61_90"] ?? 0)}             icon={ArrowUpRight} testid="fin-kpi-debtors-61-90" tone={(ar?.["61_90"] ?? 0) > 0 ? "warn" : "default"} />
        <KpiCard enterpriseId="FIN-KPI-034" label="Debtors 90+d"    value={fmtCurrency(ar?.["90_plus"] ?? 0)}           icon={ArrowUpRight} testid="fin-kpi-debtors-90-plus" tone={(ar?.["90_plus"] ?? 0) > 0 ? "danger" : "default"} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap gap-1 justify-start">
          <TabsTrigger data-testid="fin-tab-overview"    value="overview"    className="text-xs data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-pnl"          value="pnl"        className="text-xs data-[state=active]:bg-white">Profit & Loss</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-pnl-summary"   value="pnl-summary" className="text-xs data-[state=active]:bg-white">P&L Summary</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-balance-sheet" value="balance-sheet" className="text-xs data-[state=active]:bg-white">Balance Sheet</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-alloc"        value="alloc"      className="text-xs data-[state=active]:bg-white">BU Expenses Allocation</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-sales-by-bu"  value="sales-by-bu" className="text-xs data-[state=active]:bg-white">Sales by BU</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-client-profit" value="client-profit" className="text-xs data-[state=active]:bg-white">Client Profitability</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-cashflow"     value="cashflow"   className="text-xs data-[state=active]:bg-white">Cash Flow</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-debtors"      value="debtors"    className="text-xs data-[state=active]:bg-white">Debtors</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-creditors"    value="creditors"  className="text-xs data-[state=active]:bg-white">Creditors</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-stock"        value="stock"      className="text-xs data-[state=active]:bg-white">Stock Snapshots</TabsTrigger>
          <TabsTrigger data-testid="fin-tab-myob"         value="myob"       className="text-xs data-[state=active]:bg-white">MYOB</TabsTrigger>
        </TabsList>

        {/* -------- Overview -------- */}
        <TabsContent value="overview" className="mt-4">
          <PnlChart series={pnl?.series || []} />
        </TabsContent>

        {/* -------- P&L -------- */}
        <TabsContent value="pnl" className="mt-4 space-y-4">
          <PnlChart series={pnl?.series || []} />
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Month</th>
                  <th className="text-right font-medium px-3 py-2">Revenue</th>
                  <th className="text-right font-medium px-3 py-2">COGS</th>
                  <th className="text-right font-medium px-3 py-2">GP</th>
                  <th className="text-right font-medium px-3 py-2">GP %</th>
                  <th className="text-right font-medium px-3 py-2">OpEx</th>
                  <th className="text-right font-medium px-3 py-2">EBITDA</th>
                  <th className="text-center font-medium px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {(pnl?.series || []).slice().reverse().map((m) => (
                  <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{m.month}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.cogs)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.gross_profit)}</td>
                    <td className={`px-3 py-2 text-right ${m.gp_pct < 15 ? "text-rose-700" : m.gp_pct < 25 ? "text-amber-700" : "text-emerald-700"}`}>{m.gp_pct}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(m.opex)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${m.ebitda < 0 ? "text-rose-700" : "text-slate-800"}`}>{fmtCurrency(m.ebitda)}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge className={`text-[9px] ${m.source === "xero" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"}`}>{m.source}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* -------- P&L Summary (Power BI-style) -------- */}
        <TabsContent value="pnl-summary" className="mt-4">
          <PLSummaryPanel canEdit={canEditAlloc} />
        </TabsContent>

        {/* -------- Balance Sheet -------- */}
        <TabsContent value="balance-sheet" className="mt-4">
          <BalanceSheetPanel canEdit={canEditAlloc} />
        </TabsContent>

        {/* -------- BU Expenses Allocation (V2 – per-expense) -------- */}
        <TabsContent value="alloc" className="mt-4">
          <BUExpensesAllocationContent />
        </TabsContent>

        {/* -------- Sales by BU (target = budget × 1.05) -------- */}
        <TabsContent value="cashflow" className="mt-4">
          <CashFlowPanel />
        </TabsContent>

        <TabsContent value="sales-by-bu" className="mt-4">
          <SalesByBUPanel />
        </TabsContent>

        {/* -------- Client Profitability (Aroflo-sourced) -------- */}
        <TabsContent value="client-profit" className="mt-4">
          <ClientProfitabilityPanel />
        </TabsContent>

        {/* -------- Debtors -------- */}
        <TabsContent value="debtors" className="mt-4 space-y-4">
          <AgedBucketsCard title="Aged Receivables" data={ar} kind="receivables"
            enterpriseIds={{ total: "FIN-KPI-030", "0_30": "FIN-KPI-031", "31_60": "FIN-KPI-032", "61_90": "FIN-KPI-033", "90_plus": "FIN-KPI-034" }} />
          <DebtorsTable />
          <DebtorsDigestCard />
        </TabsContent>

        {/* -------- Creditors -------- */}
        <TabsContent value="creditors" className="mt-4 space-y-4">
          <AgedBucketsCard title="Aged Payables" data={ap} kind="payables"
            enterpriseIds={{ total: "FIN-KPI-037", "0_30": "FIN-KPI-037", "31_60": "FIN-KPI-037", "61_90": "FIN-KPI-037", "90_plus": "FIN-KPI-038" }} />
          <CreditorsTable />
        </TabsContent>

        {/* -------- MYOB Integration -------- */}
        <TabsContent value="stock" className="mt-4">
          <BUStockSnapshotsPanel />
        </TabsContent>

        <TabsContent value="myob" className="mt-4">
          <MYOBConnectPanel canEdit={canEditAlloc} />
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}


// ============================================================
// P&L Chart
// ============================================================
function PnlChart({ series }) {
  // Detect when all 3 lines would be identical (COGS + OpEx = 0 everywhere) —
  // Revenue overlaps GP and EBITDA. Show a hint to the user.
  const costsAllZero = series.length > 0 && series.every((m) =>
    (m.cogs || 0) === 0 && (m.opex || 0) === 0
  );
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-500 mb-3">
        <TrendingUp className="w-3.5 h-3.5" />
        <span className="text-[10px] uppercase tracking-wider font-medium">Rolling P&L (Revenue · GP · EBITDA)</span>
        <ProvenanceButton enterpriseId="FIN-KPI-008" size="xs" className="ml-auto" />
      </div>
      {costsAllZero && (
        <div className="mb-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <b>Only Revenue is populated</b> — GP and EBITDA lines overlap with Revenue because COGS and OpEx are $0.
          Upload MYOB P&amp;L Actuals (P&amp;L Summary tab) or fix Bills sync to see real GP/EBITDA trends.
        </div>
      )}
      <div className="h-72">
        {series.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-500">No P&L data available yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} tickMargin={4} interval={1} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={55} />
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="revenue"      stroke="#003a5c" strokeWidth={2} dot={false} name="Revenue" />
              {!costsAllZero && (
                <Line type="monotone" dataKey="gross_profit" stroke="#0f766e" strokeWidth={2} dot={false} name="Gross Profit" />
              )}
              {!costsAllZero && (
                <Line type="monotone" dataKey="ebitda"       stroke="#c05621" strokeWidth={2} dot={false} name="EBITDA" />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}


// ============================================================
// BU Performance table
// ============================================================
function BUPerformanceTable({ data }) {
  if (!data) return null;
  const items = data.items || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-slate-600">
        <span>Allocation method: <span className="font-medium text-slate-900">{data.allocation_method}</span></span>
        <span>·</span>
        <span>Version: v{data.allocation_version}</span>
        <span>·</span>
        <span>Group overhead pool: <span className="font-medium text-slate-900">{fmtCurrency(data.total_group_overhead)}</span></span>
        <DataStatusChip status="manager_adjusted" size="xs" className="ml-2" />
        <ProvenanceButton enterpriseId="FIN-KPI-024" size="xs" />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left font-medium px-3 py-2">Business Unit</th>
              <th className="text-right font-medium px-3 py-2">Direct Revenue</th>
              <th className="text-right font-medium px-3 py-2">Direct Costs</th>
              <th className="text-right font-medium px-3 py-2">Direct GP</th>
              <th className="text-right font-medium px-3 py-2">GP %</th>
              <th className="text-right font-medium px-3 py-2">Direct OpEx</th>
              <th className="text-right font-medium px-3 py-2">Direct Contribution</th>
              <th className="text-right font-medium px-3 py-2">Weight %</th>
              <th className="text-right font-medium px-3 py-2">Allocated Overhead</th>
              <th className="text-right font-medium px-3 py-2">Fully Allocated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.business_unit_id} data-testid={`fin-bu-row-${r.business_unit_id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-2 font-medium text-slate-800">{r.business_unit_name || r.business_unit_id}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(r.direct_revenue)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(r.direct_costs)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(r.direct_gp)}</td>
                <td className={`px-3 py-2 text-right ${r.direct_gp_pct < 15 ? "text-rose-700" : r.direct_gp_pct < 25 ? "text-amber-700" : "text-emerald-700"}`}>{r.direct_gp_pct}%</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(r.direct_opex)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(r.direct_contribution)}</td>
                <td className="px-3 py-2 text-right">{r.allocation_weight_pct}%</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtCurrency(r.allocated_overhead)}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.fully_allocated_contribution < 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmtCurrency(r.fully_allocated_contribution)}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="10" className="p-6 text-center text-sm text-slate-500">No BU-tagged rows in finance_periods yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================
// BU Allocation panel — star piece
// ============================================================
function BUAllocationPanel({ alloc, bu, onSave, canEdit }) {
  const [method, setMethod] = useState(alloc?.method || "revenue_percent");
  const [totalOH, setTotalOH] = useState(alloc?.total_group_overhead || 0);
  const [weights, setWeights] = useState(alloc?.weights || {});
  const [notes, setNotes] = useState(alloc?.notes || "");
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState(null);

  useEffect(() => {
    if (alloc) {
      setMethod(alloc.method || "revenue_percent");
      setTotalOH(alloc.total_group_overhead || 0);
      setWeights(alloc.weights || {});
      setNotes(alloc.notes || "");
    }
  }, [alloc]);

  const bus = useMemo(() => (bu?.items || []).map((b) => ({ id: b.business_unit_id, name: b.business_unit_name || b.business_unit_id })), [bu]);

  // Auto-populate weights when method=revenue_percent based on BU direct revenue
  const applyRevenueWeights = () => {
    const items = bu?.items || [];
    const total = items.reduce((a, b) => a + (b.direct_revenue || 0), 0);
    if (total <= 0) return;
    const w = {};
    items.forEach((b) => { w[b.business_unit_id] = Math.round(((b.direct_revenue || 0) / total) * 1000) / 10; });
    setWeights(w);
    toast.success("Weights populated from direct revenue share");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/finance/v1/bu-allocation", {
        method, weights, total_group_overhead: Number(totalOH) || 0, notes,
      });
      toast.success("Allocation saved (version bumped)");
      await onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const loadVersions = async () => {
    try {
      const { data } = await api.get("/finance/v1/bu-allocation/versions?limit=25");
      setVersions(data.items || []);
    } catch (e) { toast.error("Could not load versions"); }
  };

  const weightSum = Object.values(weights).reduce((a, b) => a + Number(b || 0), 0);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 flex items-start gap-3">
        <DataStatusChip status="manager_adjusted" size="md" />
        <div className="text-xs text-slate-700 leading-relaxed">
          <strong>Manager Adjusted</strong> — allocation method and weights are configured by an authorised user.
          Every change bumps the allocation <strong>version</strong> (currently <strong>v{alloc?.version || 0}</strong>) and is
          fully audited. This drives the Fully-Allocated column on BU Performance.
        </div>
        <ProvenanceButton enterpriseId="FIN-KPI-024" size="xs" className="ml-auto" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Config */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Allocation configuration</div>

          <div>
            <label className="text-xs text-slate-600 mb-1 block">Method</label>
            <Select value={method} onValueChange={setMethod} disabled={!canEdit}>
              <SelectTrigger data-testid="fin-alloc-method" className="h-9 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue_percent">Revenue % of group</SelectItem>
                <SelectItem value="headcount">Headcount share</SelectItem>
                <SelectItem value="labour_hours">Labour hours share</SelectItem>
                <SelectItem value="manual">Manual %</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-slate-600 mb-1 block">Total group overhead pool ($ / 12 mo)</label>
            <Input data-testid="fin-alloc-overhead" type="number" step="1000"
                    value={totalOH} onChange={(e) => setTotalOH(e.target.value)}
                    disabled={!canEdit} className="h-9 bg-white" />
          </div>

          <div>
            <label className="text-xs text-slate-600 mb-1 block">Notes</label>
            <Input data-testid="fin-alloc-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    disabled={!canEdit} placeholder="Why this allocation? e.g. 'FY26 board approved'" className="h-9 bg-white" />
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button data-testid="fin-alloc-save" onClick={save} disabled={!canEdit || saving}
                    className="bg-gbe hover:bg-gbe-hover text-white" size="sm">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save (v{(alloc?.version || 0) + 1})
            </Button>
            <Button variant="outline" size="sm" onClick={loadVersions} data-testid="fin-alloc-versions-btn">
              <History className="w-3.5 h-3.5 mr-1.5" /> Version history
            </Button>
          </div>
        </div>

        {/* Weights */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Weights per business unit</div>
            <span className={`ml-auto text-[10px] ${Math.abs(weightSum - 100) < 0.5 ? "text-emerald-700" : "text-amber-700"} tabular-nums`}>
              Sum: {weightSum.toFixed(1)} {Math.abs(weightSum - 100) < 0.5 ? "✓" : "⚠︎ (normalises to 100%)"}
            </span>
          </div>
          {method === "revenue_percent" && (
            <Button variant="outline" size="sm" onClick={applyRevenueWeights} disabled={!canEdit} data-testid="fin-alloc-populate">
              Populate from revenue share
            </Button>
          )}
          <div className="space-y-1.5">
            {bus.map((b) => (
              <div key={b.id} className="flex items-center gap-2">
                <span className="w-40 text-xs text-slate-700 truncate" title={b.id}>{b.name}</span>
                <Input
                  data-testid={`fin-alloc-weight-${b.id}`}
                  type="number" step="0.1" min="0"
                  value={weights[b.id] ?? ""}
                  onChange={(e) => setWeights({ ...weights, [b.id]: e.target.value })}
                  disabled={!canEdit}
                  className="h-8 w-24 bg-white text-right tabular-nums"
                />
                <span className="text-xs text-slate-500">%</span>
              </div>
            ))}
            {bus.length === 0 && <div className="text-xs text-slate-500">No BUs with direct revenue yet.</div>}
          </div>
        </div>
      </div>

      {versions && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Version history</span>
            <ProvenanceButton enterpriseId="FIN-KPI-026" size="xs" className="ml-auto" />
          </div>
          {versions.length === 0 ? (
            <div className="text-xs text-slate-500">No historic versions yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100 text-xs">
              {versions.map((v) => (
                <li key={v.id} className="py-2 flex items-center gap-3">
                  <span className="font-mono text-slate-500">v{v.version}</span>
                  <span className="text-slate-800">{v.method}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-600">{fmtCurrency(v.total_group_overhead)} overhead</span>
                  <span className="ml-auto text-slate-500">{(v.archived_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="text-slate-500">by {v.archived_by}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Aged Buckets
// ============================================================
function AgedBucketsCard({ title, data, enterpriseIds, kind = "receivables" }) {
  const [openBucket, setOpenBucket] = useState(null);   // '0_30' | ... | null
  const [drill, setDrill] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);
  if (!data) return null;
  const buckets = [
    { key: "0_30",    label: "0-30 days" },
    { key: "31_60",   label: "31-60 days" },
    { key: "61_90",   label: "61-90 days" },
    { key: "90_plus", label: "90+ days" },
  ];
  const total = data.total || 0;
  const noData = !total && (data.diagnostics?.myob_bills_total === 0 || data.diagnostics?.invoices_with_valid_due_date === 0);

  const openDrill = async (bucketKey) => {
    setOpenBucket(bucketKey);
    setDrill(null);
    setDrillBusy(true);
    try {
      const path = kind === "payables"
        ? `/finance/v1/aged-payables/drilldown?bucket=${bucketKey}`
        : `/finance/v1/aged-receivables/drilldown?bucket=${bucketKey}`;
      const { data } = await api.get(path);
      setDrill(data);
    } catch (e) {
      toast.error("Failed to load drill-down");
    } finally { setDrillBusy(false); }
  };
  const closeDrill = () => { setOpenBucket(null); setDrill(null); };
  const isReceivables = kind !== "payables";

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{title} · Total</div>
          <div className="text-3xl font-display font-semibold text-slate-900 mt-1">{fmtCurrency(total)}</div>
          {total > 0 && (
            <button type="button" onClick={() => openDrill("total")}
              data-testid={`fin-aged-drill-${kind}-total`}
              className="mt-1 text-[11px] text-gbe hover:underline">
              View all {total > 0 ? "" : ""}invoices →
            </button>
          )}
        </div>
        <ProvenanceButton enterpriseId={enterpriseIds.total} size="sm" className="ml-auto" />
      </div>

      {/* Empty-state for Creditors when MYOB Bills sync isn't running */}
      {noData && data.diagnostics?.note && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800">
          <b>Sync pending</b> — {data.diagnostics.note}
        </div>
      )}

      <div className="grid md:grid-cols-3 xl:grid-cols-4 gap-3">
        {buckets.map((b) => {
          const v = data[b.key] || 0;
          const pct = total > 0 ? (v / total) * 100 : 0;
          const tone = b.key === "90_plus" ? "border-rose-200 bg-rose-50/40"
                    : b.key === "61_90"   ? "border-amber-200 bg-amber-50/40"
                    :                       "border-slate-200 bg-white";
          const clickable = v > 0;
          return (
            <button key={b.key} type="button"
                    data-testid={`fin-aged-drill-${kind}-${b.key}`}
                    disabled={!clickable}
                    onClick={() => clickable && openDrill(b.key)}
                    className={`rounded-lg border ${tone} p-3 relative text-left transition ${clickable ? "hover:shadow-sm hover:border-slate-400 cursor-pointer" : "opacity-70 cursor-not-allowed"}`}>
              <div className="absolute top-2 right-2"><ProvenanceButton enterpriseId={enterpriseIds[b.key]} size="xs" /></div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{b.label}</div>
              <div className="text-xl font-display font-semibold text-slate-900 mt-1">{fmtCurrency(v)}</div>
              <div className="mt-2 h-1 bg-slate-200 rounded overflow-hidden">
                <div className={`h-full ${b.key === "90_plus" ? "bg-rose-500" : b.key === "61_90" ? "bg-amber-500" : "bg-emerald-500"}`}
                     style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                <span>{pct.toFixed(1)}%</span>
                {clickable && <span className="ml-auto text-gbe">Click to drill →</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Drill-down sheet */}
      <Sheet open={!!openBucket} onOpenChange={(o) => { if (!o) closeDrill(); }}>
        <SheetContent className="w-full sm:max-w-4xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{title} · {buckets.find((b) => b.key === openBucket)?.label || "All"}</SheetTitle>
            <SheetDescription>
              {drill ? `${isReceivables ? drill.invoice_count : drill.bill_count} ${isReceivables ? "invoices" : "bills"} · ${fmtCurrency(drill.total_amount)}` : "Loading…"}
            </SheetDescription>
          </SheetHeader>
          {drillBusy && <div className="mt-4 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading drill-down…</div>}
          {drill && (
            <div className="mt-4 space-y-4">
              {/* By customer/supplier rollup */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  By {isReceivables ? "Customer" : "Supplier"} ({(isReceivables ? drill.by_customer : drill.by_supplier).length})
                </div>
                <div className="max-h-[240px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left font-medium px-3 py-1.5">{isReceivables ? "Customer" : "Supplier"}</th>
                        <th className="text-right font-medium px-3 py-1.5">{isReceivables ? "Invoices" : "Bills"}</th>
                        <th className="text-right font-medium px-3 py-1.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isReceivables ? drill.by_customer : drill.by_supplier).map((r, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="px-3 py-1.5 font-medium text-slate-800">{r.customer || r.supplier}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.invoice_count || r.bill_count}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmtCurrency(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Line-level */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  {isReceivables ? "Invoice detail" : "Bill detail"} ({(isReceivables ? drill.invoices : drill.bills).length}{(isReceivables ? drill.invoices_truncated : drill.bills_truncated) ? " · capped at 500" : ""})
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="text-left font-medium px-3 py-1.5">{isReceivables ? "Customer" : "Supplier"}</th>
                        <th className="text-left font-medium px-3 py-1.5">{isReceivables ? "Invoice #" : "Bill #"}</th>
                        <th className="text-left font-medium px-3 py-1.5">Date</th>
                        <th className="text-left font-medium px-3 py-1.5">Due</th>
                        <th className="text-right font-medium px-3 py-1.5">Days overdue</th>
                        <th className="text-right font-medium px-3 py-1.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isReceivables ? drill.invoices : drill.bills).map((row, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="px-3 py-1.5 font-medium text-slate-800">{row.customer || row.supplier}</td>
                          <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">{row.invoice_number || row.bill_number}</td>
                          <td className="px-3 py-1.5 text-slate-500">{(row.date || "").slice(0, 10)}</td>
                          <td className="px-3 py-1.5 text-slate-500">{(row.due_date || "").slice(0, 10)}</td>
                          <td className={`px-3 py-1.5 text-right tabular-nums ${row.days_overdue > 60 ? "text-rose-700" : row.days_overdue > 30 ? "text-amber-700" : "text-slate-600"}`}>
                            {row.days_overdue}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmtCurrency(row.amount_due)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}



// ============================================================
// BU Performance — Explainer
// ============================================================
function BUPerformanceExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
              data-testid="fin-bu-performance-help-toggle"
              className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 hover:text-slate-900">
        <Info className="w-3.5 h-3.5 text-gbe" />
        <span>What is BU Performance?</span>
        <span className="ml-auto text-[10px] text-slate-500">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-700 leading-relaxed space-y-1.5"
             data-testid="fin-bu-performance-help-body">
          <p><b>BU Performance</b> shows how each Business Unit is trading on its <b>own direct P&amp;L</b> — before group overhead is spread across the group.</p>
          <ul className="ml-4 list-disc space-y-1">
            <li><b>Direct Revenue</b> — sales attributed to the BU via <span className="font-mono bg-white border border-slate-200 px-1 rounded">MYOB → BU Auto-suggest</span> (income accounts mapped to a BU).</li>
            <li><b>Direct Costs</b> — Cost-of-Sales accounts mapped to that BU (Materials, Wages, Subs, Labour Hire…).</li>
            <li><b>Direct OpEx</b> — Expense accounts mapped to that BU.</li>
            <li><b>Direct Contribution</b> = Direct Revenue − Direct Costs − Direct OpEx.</li>
            <li><b>Allocated Overhead</b> — group-level overhead applied to the BU using the driver + weights configured in <b>BU Allocation</b>.</li>
            <li><b>Fully Allocated</b> = Direct Contribution − Allocated Overhead.</li>
          </ul>
          <p className="pt-1 text-slate-600"><b>Why does a BU show no data?</b></p>
          <ul className="ml-4 list-disc space-y-1">
            <li>No Income / COGS / Expense accounts have been mapped to it in <span className="font-mono bg-white border border-slate-200 px-1 rounded">MYOB → BU Auto-suggest</span>.</li>
            <li>Or the BU is inactive but still in the register (empty row = no mapped activity, not a bug).</li>
            <li>Fix by opening the <b>MYOB</b> tab and running <i>Auto-suggest</i>, then confirming the account → BU pairings.</li>
          </ul>
        </div>
      )}
    </div>
  );
}


// ============================================================
// BU Allocation — Explainer
// ============================================================
function BUAllocationExplainer() {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
      <button type="button" onClick={() => setOpen((v) => !v)}
              data-testid="fin-bu-alloc-help-toggle"
              className="w-full flex items-center gap-2 text-xs font-medium text-slate-800 hover:text-slate-900">
        <Info className="w-3.5 h-3.5 text-emerald-700" />
        <span>What is BU Allocation?</span>
        <span className="ml-auto text-[10px] text-slate-600">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-emerald-200 text-[11px] text-slate-700 leading-relaxed space-y-1.5"
             data-testid="fin-bu-alloc-help-body">
          <p><b>BU Allocation</b> is the engine that spreads <b>group overhead</b> (e.g., head office admin, insurances, IT) across the Business Units so we can see each BU's <b>fully-allocated contribution</b> — not just direct margin.</p>
          <ul className="ml-4 list-disc space-y-1">
            <li><b>Method</b> — the driver used to spread the pool:
              <ul className="ml-4 list-[circle] mt-0.5">
                <li><b>Revenue %</b> — proportional to each BU's share of direct revenue (default & most common).</li>
                <li><b>Headcount</b> — proportional to BU staff count.</li>
                <li><b>Labour hours</b> — proportional to billable hours.</li>
                <li><b>Manual %</b> — you type the weights yourself (board-directed splits).</li>
              </ul>
            </li>
            <li><b>Total group overhead pool</b> — the annual $ amount to spread (from Finance → Group P&amp;L overhead lines).</li>
            <li><b>Weights per BU</b> — auto-populate from revenue share, or override manually. Weights are normalised to 100% when saving.</li>
            <li><b>Version history</b> — every save bumps the version and is fully audited (who / when / value). This drives the <b>Fully Allocated</b> column on BU Performance.</li>
          </ul>
          <p className="pt-1 text-slate-600"><b>Classification:</b> Manager Adjusted (per Ver1 spec). Only Finance / Executive / Admin roles can edit.</p>
        </div>
      )}
    </div>
  );
}


// ============================================================
// Debtors Detail Table (All / By Client / By BU)
// ============================================================
function DebtorsTable() {
  const [groupBy, setGroupBy] = useState("bu_client");
  const [includeCurrent, setIncludeCurrent] = useState(true);
  const [only180, setOnly180] = useState(false);
  const [asAt, setAsAt] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tagging, setTagging] = useState(false);
  const [allBus, setAllBus] = useState([]);
  const [tagClient, setTagClient] = useState(null);   // { customer, selected:Set }
  const [tagSaving, setTagSaving] = useState(false);
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followup, setFollowup] = useState(null);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [followupBu, setFollowupBu] = useState("all");
  const [followupMinDays, setFollowupMinDays] = useState(1);

  const loadFollowup = async (buId = followupBu, minDays = followupMinDays) => {
    setFollowupLoading(true);
    try {
      const { data } = await api.get("/finance/v1/debtors/bu-followup", {
        params: { min_days: minDays || 1, ...(buId && buId !== "all" ? { bu_id: buId } : {}) },
      });
      setFollowup(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load BU follow-up report");
    } finally { setFollowupLoading(false); }
  };
  const openFollowup = () => { setFollowupOpen(true); loadFollowup("all", 1); setFollowupBu("all"); setFollowupMinDays(1); };
  const exportFollowupCSV = (group) => {
    const cols = ["customer", "invoice_number", "due_date", "days_overdue", "amount_due", "bucket", "source"];
    const csv = [
      cols.join(","),
      ...group.invoices.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `overdue_${group.bu_code || "unallocated"}.csv`;
    a.click();
  };

  useEffect(() => {
    api.get("/org/business-units")
      .then((r) => setAllBus((r.data?.items || r.data || []).filter((b) => b.id || b._id)))
      .catch(() => {});
  }, []);

  const openTag = (customer, buIds) => {
    setTagClient({ customer, selected: new Set((buIds || []).map(String)) });
  };
  const toggleTagBu = (id) => setTagClient((p) => {
    const s = new Set(p.selected);
    s.has(id) ? s.delete(id) : s.add(id);
    return { ...p, selected: s };
  });
  const saveTag = async () => {
    setTagSaving(true);
    try {
      await api.post("/finance/v1/debtors/tag-client", {
        customer: tagClient.customer,
        business_unit_ids: [...tagClient.selected],
      });
      toast.success(`${tagClient.customer} linked to ${tagClient.selected.size || "no"} BU${tagClient.selected.size === 1 ? "" : "s"}`);
      setTagClient(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save BU link");
    } finally { setTagSaving(false); }
  };

  const autoTag = async () => {
    setTagging(true);
    try {
      const { data } = await api.post("/master/clients/bu-autotag");
      toast.success(`Tagged ${data.tagged} client(s) · ${data.clients_still_untagged} still untagged`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-tag failed");
    } finally { setTagging(false); }
  };
  const [resolving, setResolving] = useState(false);
  const resolveInvoiceBus = async () => {
    setResolving(true);
    try {
      const { data } = await api.post("/finance/v1/debtors/resolve-invoice-bu");
      toast.success(`Resolved ${data.invoices_resolved_to_bu} of ${data.invoices_with_job} invoices to a BU (via job owner)`);
      if (data.backfill_started) {
        toast.info("Pulling invoice→job links from AroFlo in the background — BU allocations will update shortly. Refresh in a minute.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolve failed");
    } finally { setResolving(false); }
  };

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/finance/v1/aged-receivables/table", {
        params: { group_by: groupBy, include_current: includeCurrent, min_days: only180 ? 181 : 0,
                  ...(asAt ? { as_at: asAt } : {}) },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load debtors table");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupBy, includeCurrent, only180, asAt]);

  const rows = data?.rows || [];
  const exportCSV = () => {
    if (!rows.length) return;
    const cols = Object.keys(rows[0]).filter((k) => !["bu_ids", "business_unit_id"].includes(k));
    const csv = [cols.join(","), ...rows.map((r) =>
      cols.map((c) => JSON.stringify(r[c] ?? "")).join(",")
    )].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `debtors_${groupBy}${only180 ? "_180plus" : ""}${asAt ? `_asat_${asAt}` : ""}.csv`;
    a.click();
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Filter className="w-4 h-4" /> Debtors detail
          </div>
          <div className="text-[11px] text-slate-500">
            {data ? `${data.row_count} invoice${data.row_count === 1 ? "" : "s"} · ${fmtCurrency(data.grand_total)} outstanding` : "Loading…"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]">
            {[
              { v: "bu_client", label: "BU → Client", icon: Building2 },
              { v: "customer",  label: "By Client",   icon: Users },
              { v: "bu",        label: "By BU",       icon: Building2 },
              { v: "all",       label: "All GB",      icon: DollarSign },
            ].map((o) => (
              <button key={o.v}
                      data-testid={`fin-debtors-group-${o.v}`}
                      onClick={() => setGroupBy(o.v)}
                      className={`px-2.5 py-1 flex items-center gap-1 border-r border-slate-200 last:border-r-0 transition ${
                        groupBy === o.v ? "bg-gbe text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}>
                <o.icon className="w-3 h-3" />
                {o.label}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" checked={includeCurrent} onChange={(e) => setIncludeCurrent(e.target.checked)}
                   data-testid="fin-debtors-include-current"
                   className="w-3.5 h-3.5" />
            Include current (not yet overdue)
          </label>
          <label className={`inline-flex items-center gap-1.5 text-[11px] cursor-pointer ${only180 ? "text-rose-700 font-semibold" : "text-slate-600"}`}>
            <input type="checkbox" checked={only180} onChange={(e) => setOnly180(e.target.checked)}
                   data-testid="fin-debtors-180-only"
                   className="w-3.5 h-3.5 accent-rose-600" />
            180+ days only
          </label>
          <label className={`inline-flex items-center gap-1.5 text-[11px] ${asAt ? "text-gbe font-semibold" : "text-slate-600"}`}>
            As at
            <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)}
                   data-testid="fin-debtors-as-at"
                   className="h-7 px-1.5 border border-slate-200 rounded-md text-[11px] bg-white" />
            {asAt && (
              <button onClick={() => setAsAt("")} data-testid="fin-debtors-as-at-clear"
                      className="text-slate-400 hover:text-slate-600" title="Back to today">✕</button>
            )}
          </label>
          <Button size="sm" variant="outline" onClick={exportCSV} disabled={!rows.length}
                  data-testid="fin-debtors-export">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
          <Button size="sm" variant="outline" onClick={autoTag} disabled={tagging}
                  data-testid="fin-debtors-autotag" title="Tag untagged clients with BUs derived from contracts and completed tasks">
            {tagging ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Building2 className="w-3.5 h-3.5 mr-1" />}
            Auto-tag BUs
          </Button>
          <Button size="sm" variant="outline" onClick={resolveInvoiceBus} disabled={resolving}
                  data-testid="fin-debtors-resolve-invoice-bu" title="Allocate each invoice to a BU via its AroFlo job's task owner">
            {resolving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Building2 className="w-3.5 h-3.5 mr-1" />}
            Resolve invoice BUs
          </Button>
          <Button size="sm" onClick={openFollowup}
                  className="bg-gbe text-white hover:bg-gbe/90"
                  data-testid="fin-debtors-bu-followup-open" title="Per-BU overdue debtors to chase, for manager follow-up">
            <AlertCircle className="w-3.5 h-3.5 mr-1" />
            BU follow-up
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="fin-debtors-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {rows.length === 0 && !busy && (
        <div className="text-[11px] text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded">
          No outstanding invoices found.
        </div>
      )}

      {rows.length > 0 && groupBy !== "all" && (
        <div className="border border-slate-200 rounded overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                {groupBy === "bu_client" && <th className="text-left px-3 py-2">Business Unit</th>}
                <th className="text-left px-3 py-2">{groupBy === "bu" ? "Business Unit" : "Client"}</th>
                <th className="text-right px-3 py-2">Invoices</th>
                <th className="text-right px-3 py-2">0-30d</th>
                <th className="text-right px-3 py-2">31-60d</th>
                <th className="text-right px-3 py-2">61-90d</th>
                <th className="text-right px-3 py-2">91-180d</th>
                <th className="text-right px-3 py-2">180+d</th>
                <th className="text-right px-3 py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50"
                    data-testid={`fin-debtors-row-${i}`}>
                  {groupBy === "bu_client" && (
                    <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">
                      <span className="font-mono text-[10px] text-slate-500 mr-1">{r.bu_code || "—"}</span>
                      {r.bu_name || "Unallocated"}
                    </td>
                  )}
                  <td className="px-3 py-1.5 font-medium text-slate-800">
                    {groupBy === "bu" ? (
                      <span>
                        <span className="font-mono text-[10px] text-slate-500 mr-1">{r.bu_code || "—"}</span>
                        {r.bu_name || "Unallocated"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        {r.customer}
                        <button
                          onClick={() => openTag(r.customer, groupBy === "bu_client"
                            ? (r.business_unit_id ? [r.business_unit_id] : [])
                            : (r.bu_ids || []))}
                          className="text-[10px] text-sky-600 hover:text-sky-800 hover:underline"
                          data-testid={`fin-debtors-tagbu-${i}`}>
                          Tag BU
                        </button>
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.count}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtCurrency(r["0_30"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["31_60"] > 0 ? "text-amber-700" : "text-slate-400"}`}>{fmtCurrency(r["31_60"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["61_90"] > 0 ? "text-amber-700" : "text-slate-400"}`}>{fmtCurrency(r["61_90"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["91_180"] > 0 ? "text-rose-700" : "text-slate-400"}`}>{fmtCurrency(r["91_180"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["180_plus"] > 0 ? "text-rose-800 font-semibold" : "text-slate-400"}`}>{fmtCurrency(r["180_plus"])}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-900">{fmtCurrency(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr className="font-semibold text-slate-900" data-testid="fin-debtors-totals-row">
                {groupBy === "bu_client" && <td className="px-3 py-2">TOTAL</td>}
                <td className="px-3 py-2">{groupBy === "bu_client" ? `${rows.length} rows` : "TOTAL"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{rows.reduce((a, r) => a + (r.count || 0), 0)}</td>
                {["0_30", "31_60", "61_90", "91_180", "180_plus"].map((k) => (
                  <td key={k} className={`px-3 py-2 text-right tabular-nums ${k === "180_plus" ? "text-rose-800" : ""}`}>
                    {fmtCurrency(rows.reduce((a, r) => a + (r[k] || 0), 0))}
                  </td>
                ))}
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(rows.reduce((a, r) => a + (r.total || 0), 0))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {rows.length > 0 && groupBy === "all" && (
        <div className="border border-slate-200 rounded overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Invoice #</th>
                <th className="text-left px-3 py-2">BU</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-right px-3 py-2">Days OD</th>
                <th className="text-right px-3 py-2">Bucket</th>
                <th className="text-right px-3 py-2 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.customer}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">{r.invoice_number}</td>
                  <td className="px-3 py-1.5 text-slate-500 font-mono text-[10px]">{(r.bu_codes || []).join(", ") || "—"}</td>
                  <td className="px-3 py-1.5 text-slate-500">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{(r.due_date || "").slice(0, 10)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r.days_overdue > 180 ? "text-rose-800 font-semibold" : r.days_overdue > 60 ? "text-rose-700" : r.days_overdue > 30 ? "text-amber-700" : "text-slate-600"}`}>
                    {r.days_overdue}
                  </td>
                  <td className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500">{r.bucket.replace("_", "-")}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtCurrency(r.amount_due)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr className="font-semibold text-slate-900" data-testid="fin-debtors-flat-totals-row">
                <td className="px-3 py-2">TOTAL</td>
                <td className="px-3 py-2 text-slate-500">{rows.length} invoices</td>
                <td colSpan={5}></td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtCurrency(rows.reduce((a, r) => a + (r.amount_due || 0), 0))}</td>
              </tr>
            </tfoot>
          </table>
          {data?.truncated && (
            <div className="text-[10px] text-slate-400 px-3 py-1.5 bg-slate-50 border-t border-slate-200">
              First 1,000 of {data.row_count} rows shown. Use grouping or CSV export for full list.
            </div>
          )}
        </div>
      )}

      <Dialog open={!!tagClient} onOpenChange={(o) => !o && setTagClient(null)}>
        <DialogContent className="max-w-md" data-testid="fin-debtors-tag-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Link “{tagClient?.customer}” to Business Unit(s)</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            This client's outstanding invoices will roll up to the selected BUs. Pick more than
            one and the total splits evenly across them.
          </p>
          <div className="max-h-72 overflow-y-auto space-y-1.5 py-1">
            {allBus.map((b) => {
              const id = String(b.id || b._id);
              return (
                <label key={id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
                       data-testid={`fin-tag-bu-${id}`}>
                  <Checkbox checked={tagClient?.selected?.has(id)} onCheckedChange={() => toggleTagBu(id)} />
                  <span className="font-mono text-[10px] text-slate-500 w-14">{b.code || b.business_unit_code || ""}</span>
                  <span className="text-slate-700">{b.name || b.business_unit_name || id}</span>
                </label>
              );
            })}
            {allBus.length === 0 && <div className="text-xs text-slate-400 px-2">No business units found.</div>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTagClient(null)} disabled={tagSaving}>Cancel</Button>
            <Button onClick={saveTag} disabled={tagSaving} data-testid="fin-debtors-tag-save"
                    className="bg-slate-900 hover:bg-slate-800 text-white">
              {tagSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Save link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={followupOpen} onOpenChange={setFollowupOpen}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="fin-debtors-followup-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4 text-gbe" /> Overdue debtors by Business Unit
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap text-[11px] border-b border-slate-100 pb-3">
            <span className="text-slate-500">Business Unit</span>
            <Select value={followupBu} onValueChange={(v) => { setFollowupBu(v); loadFollowup(v, followupMinDays); }}>
              <SelectTrigger className="h-7 w-56 text-[11px]" data-testid="fin-followup-bu-select">
                <SelectValue placeholder="All BUs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All business units</SelectItem>
                {allBus.map((b) => {
                  const id = String(b.id || b._id);
                  return <SelectItem key={id} value={id}>{b.code || ""} — {b.name || id}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <span className="text-slate-500 ml-2">Overdue by at least</span>
            <Input type="number" min={1} value={followupMinDays}
                   onChange={(e) => setFollowupMinDays(Number(e.target.value) || 1)}
                   onBlur={() => loadFollowup(followupBu, followupMinDays)}
                   className="h-7 w-16 text-[11px]" data-testid="fin-followup-mindays" />
            <span className="text-slate-500">days</span>
            {followup && (
              <span className="ml-auto text-slate-600 font-medium" data-testid="fin-followup-grand-total">
                {followup.bu_count} BU{followup.bu_count === 1 ? "" : "s"} · {fmtCurrency(followup.grand_total)} overdue
              </span>
            )}
          </div>

          <div className="overflow-y-auto flex-1 space-y-3 pt-1">
            {followupLoading && (
              <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
              </div>
            )}
            {!followupLoading && followup && followup.groups.length === 0 && (
              <div className="text-center py-10 text-sm text-slate-400" data-testid="fin-followup-empty">
                No overdue invoices found. (Outstanding balances live in MYOB/Xero — resolve invoice BUs and ensure MYOB/Xero are synced.)
              </div>
            )}
            {!followupLoading && followup && followup.groups.map((g) => (
              <div key={g.business_unit_id || "unalloc"} className="rounded-lg border border-slate-200"
                   data-testid={`fin-followup-group-${g.bu_code || "unalloc"}`}>
                <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 rounded-t-lg">
                  <div className="flex items-center gap-2">
                    {g.bu_code && <span className="font-mono text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">{g.bu_code}</span>}
                    <span className="text-sm font-semibold text-slate-800">{g.bu_name}</span>
                    <span className="text-[11px] text-slate-500">· {g.summary.count} invoice{g.summary.count === 1 ? "" : "s"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-rose-700">{fmtCurrency(g.summary.total)}</span>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                            onClick={() => exportFollowupCSV(g)} data-testid={`fin-followup-csv-${g.bu_code || "unalloc"}`}>
                      <Download className="w-3 h-3 mr-1" /> CSV
                    </Button>
                  </div>
                </div>
                <div className="px-3 py-1.5 flex gap-3 flex-wrap text-[10px] text-slate-500 border-b border-slate-100">
                  {[["0_30", "0–30"], ["31_60", "31–60"], ["61_90", "61–90"], ["91_180", "91–180"], ["180_plus", "180+"]].map(([k, lbl]) => (
                    g.summary[k] > 0 && <span key={k}>{lbl}d: <span className="text-slate-700 font-medium">{fmtCurrency(g.summary[k])}</span></span>
                  ))}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="px-3 py-1.5 font-medium">Customer</th>
                        <th className="px-2 py-1.5 font-medium">Invoice</th>
                        <th className="px-2 py-1.5 font-medium">Due</th>
                        <th className="px-2 py-1.5 font-medium text-right">Days</th>
                        <th className="px-3 py-1.5 font-medium text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.invoices.slice(0, 200).map((inv, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-3 py-1.5 text-slate-700">{inv.customer}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-500">{inv.invoice_number}</td>
                          <td className="px-2 py-1.5 text-slate-500">{(inv.due_date || "").slice(0, 10)}</td>
                          <td className={`px-2 py-1.5 text-right font-medium ${inv.days_overdue > 180 ? "text-rose-600" : inv.days_overdue > 90 ? "text-amber-600" : "text-slate-600"}`}>{inv.days_overdue}</td>
                          <td className="px-3 py-1.5 text-right font-medium text-slate-800">{fmtCurrency(inv.amount_due)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {g.invoices.length > 200 && <div className="px-3 py-1.5 text-[10px] text-slate-400">Showing top 200 of {g.invoices.length}. Export CSV for the full list.</div>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}


// ============================================================
// Weekly 180+ Debtors Digest config
// ============================================================
function DebtorsDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    api.get("/finance/debtors-digest/config")
      .then(({ data }) => { setCfg(data); setEmails((data.recipients || []).join(", ")); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);
  if (denied || !cfg) return null;

  const parseEmails = () => emails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const save = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await api.put("/finance/debtors-digest/config",
        { ...cfg, enabled: enabled ?? cfg.enabled, recipients: parseEmails() });
      setCfg(data);
      toast.success(data.enabled ? "Weekly digest enabled (Mondays)" : "Digest settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/finance/debtors-digest/send-now",
        { recipients: parseEmails() });
      toast.success(`Digest sent — ${data.ok_count} delivered${data.fail_count ? `, ${data.fail_count} failed` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Mail className="w-4 h-4" /> Weekly 180+ Debtors Digest
            {cfg.enabled && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Active · Mondays</Badge>}
          </div>
          <div className="text-[11px] text-slate-500">Emails the finance team a BU → client summary of all debt outstanding beyond 180 days.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={emails} onChange={(e) => setEmails(e.target.value)}
                 placeholder="finance@gbegroup.com.au, darren@…"
                 data-testid="debtors-digest-recipients"
                 className="h-8 text-xs w-72" />
          <Button size="sm" variant="outline" onClick={() => save()} disabled={saving}
                  data-testid="debtors-digest-save">Save</Button>
          <Button size="sm" variant={cfg.enabled ? "outline" : "default"}
                  className={cfg.enabled ? "" : "bg-gbe hover:bg-gbe/90"}
                  onClick={() => save(!cfg.enabled)} disabled={saving}
                  data-testid="debtors-digest-toggle">
            {cfg.enabled ? "Disable" : "Enable weekly"}
          </Button>
          <Button size="sm" variant="outline" onClick={sendNow} disabled={sending || !parseEmails().length}
                  data-testid="debtors-digest-send-now">
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
            Send now
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Creditors Detail Table (All / By Supplier)
// ============================================================
function CreditorsTable() {
  const [groupBy, setGroupBy] = useState("supplier");
  const [includeCurrent, setIncludeCurrent] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/finance/v1/aged-payables/table", {
        params: { group_by: groupBy, include_current: includeCurrent },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load creditors table");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupBy, includeCurrent]);

  const rows = data?.rows || [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Filter className="w-4 h-4" /> Creditors detail
          </div>
          <div className="text-[11px] text-slate-500">
            {data ? `${data.row_count} bill${data.row_count === 1 ? "" : "s"} · ${fmtCurrency(data.grand_total)} owing` : "Loading…"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]">
            {[{ v: "supplier", label: "By Supplier", icon: Users },
              { v: "all",      label: "All Bills",    icon: DollarSign }].map((o) => (
              <button key={o.v}
                      data-testid={`fin-creditors-group-${o.v}`}
                      onClick={() => setGroupBy(o.v)}
                      className={`px-2.5 py-1 flex items-center gap-1 border-r border-slate-200 last:border-r-0 transition ${
                        groupBy === o.v ? "bg-gbe text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                      }`}>
                <o.icon className="w-3 h-3" />
                {o.label}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" checked={includeCurrent} onChange={(e) => setIncludeCurrent(e.target.checked)}
                   data-testid="fin-creditors-include-current"
                   className="w-3.5 h-3.5" />
            Include current
          </label>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="fin-creditors-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {rows.length === 0 && !busy && (
        <div className="text-[11px] text-slate-700 py-4 border border-dashed border-amber-300 bg-amber-50/60 rounded px-3 leading-relaxed">
          <b className="text-amber-900">No creditor data yet.</b> MYOB Purchase API is blocked for this file (Company File token required).
          Fastest path to unlock this tab:
          <ol className="mt-1.5 ml-4 list-decimal space-y-0.5">
            <li>Open <b>MYOB → Reports → Purchases → Bills [Detail]</b>.</li>
            <li>Export as CSV or XLSX.</li>
            <li>Go to <b>Import Hub</b>, pick the <b>MYOB Bills (Creditors)</b> recipe, and upload.</li>
          </ol>
          <p className="mt-1.5">Or run <span className="font-mono bg-white px-1 rounded border border-slate-200">POST /api/myob/probe/bills</span> once IT unblocks Company File tokens.</p>
        </div>
      )}

      {rows.length > 0 && groupBy === "supplier" && (
        <div className="border border-slate-200 rounded overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Supplier</th>
                <th className="text-right px-3 py-2">Bills</th>
                <th className="text-right px-3 py-2">0-30d</th>
                <th className="text-right px-3 py-2">31-60d</th>
                <th className="text-right px-3 py-2">61-90d</th>
                <th className="text-right px-3 py-2">90+d</th>
                <th className="text-right px-3 py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.supplier}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{r.count}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtCurrency(r["0_30"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["31_60"] > 0 ? "text-amber-700" : "text-slate-400"}`}>{fmtCurrency(r["31_60"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["61_90"] > 0 ? "text-amber-700" : "text-slate-400"}`}>{fmtCurrency(r["61_90"])}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r["90_plus"] > 0 ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{fmtCurrency(r["90_plus"])}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-900">{fmtCurrency(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && groupBy === "all" && (
        <div className="border border-slate-200 rounded overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Supplier</th>
                <th className="text-left px-3 py-2">Bill #</th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Due</th>
                <th className="text-right px-3 py-2">Days OD</th>
                <th className="text-right px-3 py-2">Bucket</th>
                <th className="text-right px-3 py-2 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.supplier}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">{r.bill_number}</td>
                  <td className="px-3 py-1.5 text-slate-500">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{(r.due_date || "").slice(0, 10)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${r.days_overdue > 180 ? "text-rose-800 font-semibold" : r.days_overdue > 60 ? "text-rose-700" : r.days_overdue > 30 ? "text-amber-700" : "text-slate-600"}`}>
                    {r.days_overdue}
                  </td>
                  <td className="px-3 py-1.5 text-right text-[10px] uppercase text-slate-500">{r.bucket.replace("_", "-")}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtCurrency(r.amount_due)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
